/**
 * apiRetry — silent one-shot retry helper for transient upstream failures.
 *
 * Motivating case: /api/mycraftz-install/ideas-translate makes a cold
 * Anthropic call that can exceed Cloudflare's 30-second upstream response
 * timeout, giving the client a 504 while the backend actually completes
 * and populates the Mongo cache. A single retry ~2s later hits the freshly
 * populated cache and returns instantly. Same rationale for 502/503 blips
 * during backend restarts and low-level network dropouts.
 *
 * Deliberate scope:
 *   • ONE retry only. If the retry also fails, throw. Users still get the
 *     fail-open English fallback via the caller's own try/catch.
 *   • Only retry when the error is genuinely retryable (5xx or a network
 *     error). 4xx passes straight through — a 400/422/429 is not a "cold
 *     cache" symptom and retrying it wastes cycles and rate-limit budget.
 *   • Silent to the user — no UI-facing state; the surrounding
 *     "Translating…" banner already covers the extra ~2s.
 */

const TRANSIENT_STATUSES = new Set([500, 502, 503, 504]);

/**
 * Determine whether an axios/fetch-style error is worth a single retry.
 * Handles both axios errors (with .response.status) and native fetch/
 * network errors (which have no status at all).
 */
function isTransient(err) {
  if (!err) return false;
  // Axios shape
  const status = err?.response?.status;
  if (status && TRANSIENT_STATUSES.has(status)) return true;
  // Network-level (no response received, request setup failed, etc.).
  // axios sets err.request when the request went out but got no reply,
  // and err.code === 'ECONNABORTED' on timeouts.
  if (!status && (err?.request || err?.code === "ECONNABORTED" || err?.message?.toLowerCase().includes("network"))) {
    return true;
  }
  return false;
}

/**
 * Run `fn`. On a transient failure, wait `delayMs` and try once more.
 * The second failure (or any non-transient failure) is thrown to the
 * caller, whose surrounding try/catch keeps the fail-open UX intact.
 *
 * @template T
 * @param {() => Promise<T>} fn
 * @param {{ delayMs?: number }} [opts]
 * @returns {Promise<T>}
 */
export async function withTransientRetry(fn, { delayMs = 2000 } = {}) {
  try {
    return await fn();
  } catch (err) {
    if (!isTransient(err)) throw err;
    await new Promise((r) => setTimeout(r, delayMs));
    return await fn();
  }
}
